import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { AdminInstructorEditorFields } from './AdminInstructorsCreatePage';
import AdminPersonLedgerSection from './AdminPersonLedgerSection';
import {
  buildYowanPersonOptions,
  fetchYowanAdminPersonDetail,
  fetchYowanAdminInstructorDetail,
  fetchYowanAdminMemberOptions,
  fetchYowanAdminPersonList,
  ensureYowanAdminMemberMembership,
  getYowanAdminMutationErrorMessage,
  inviteYowanAdminInstructorAccess,
  resolveYowanAdminPersonId,
  updateYowanAdminInstructor,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const instructorMemberPlaceholderOptions = [{ value: '', label: '未紐づき / 外部講師' }];

const mergeCurrentMemberOption = (options = [], record = null) => {
  if (!record?.memberId) {
    return options;
  }

  const currentOption = {
    value: record.memberId,
    label: `${record.memberName || record.member?.name || '氏名未設定'} / ${record.memberEmail || record.member?.email || '-'}`,
  };

  return options.some((option) => option.value === currentOption.value)
    ? options
    : [currentOption, ...options];
};

const preferFilledPersonValue = (primary, fallback) => {
  const value = String(primary || '').trim();

  return value ? primary : fallback || '';
};

const mergePersonLedgerRecord = (partialPerson = null, fullPerson = null) => {
  if (!partialPerson) {
    return fullPerson || null;
  }

  if (!fullPerson) {
    return partialPerson;
  }

  return {
    ...partialPerson,
    ...fullPerson,
    id: fullPerson.id || partialPerson.id,
    displayName: preferFilledPersonValue(fullPerson.displayName, partialPerson.displayName),
    nameKana: preferFilledPersonValue(fullPerson.nameKana, partialPerson.nameKana),
    email: preferFilledPersonValue(fullPerson.email, partialPerson.email),
    phone: preferFilledPersonValue(fullPerson.phone, partialPerson.phone),
    organization: preferFilledPersonValue(fullPerson.organization, partialPerson.organization),
    roleTitle: preferFilledPersonValue(fullPerson.roleTitle, partialPerson.roleTitle),
    internalNote: preferFilledPersonValue(fullPerson.internalNote, partialPerson.internalNote),
    isActive: fullPerson.isActive !== false && partialPerson.isActive !== false,
  };
};

const resolveRecordPersonId = (record = {}) =>
  record.personId ||
  record.person?.id ||
  '';

const findMatchingPersonLedger = (people = [], record = {}) => {
  const personId = resolveRecordPersonId(record);

  if (personId) {
    return people.find((person) => person.id === personId) || null;
  }

  const personEmail = String(record.person?.email || record.instructorEmail || '').trim().toLowerCase();

  if (personEmail) {
    return people.find((person) => String(person.email || '').trim().toLowerCase() === personEmail) || null;
  }

  const personName = String(record.person?.displayName || record.instructorName || '').trim();

  return personName
    ? people.find((person) => String(person.displayName || '').trim() === personName) || null
    : null;
};

const applyPersonLedgerValues = (setValue, person = null) => {
  if (!setValue || !person) {
    return;
  }

  setValue('personId', person.id || '');
  setValue('personDisplayName', person.displayName || '');
  setValue('personNameKana', person.nameKana || '');
  setValue('personEmail', person.email || '');
  setValue('personPhone', person.phone || '');
  setValue('personOrganization', person.organization || '');
  setValue('personRoleTitle', person.roleTitle || '');
  setValue('personInternalNote', person.internalNote || '');
  setValue('personIsActive', person.isActive !== false);
};

const hasSamePersonLedgerValues = (left = null, right = null) => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return [
    'id',
    'displayName',
    'nameKana',
    'email',
    'phone',
    'organization',
    'roleTitle',
    'internalNote',
  ].every((key) => String(left[key] || '') === String(right[key] || '')) &&
    (left.isActive !== false) === (right.isActive !== false);
};

const getPersonLedgerDefaults = (record = {}) => ({
  personId: resolveRecordPersonId(record),
  personDisplayName: record.person?.displayName || '',
  personNameKana: record.person?.nameKana || '',
  personEmail: record.person?.email || '',
  personPhone: record.person?.phone || '',
  personOrganization: record.person?.organization || '',
  personRoleTitle: record.person?.roleTitle || '',
  personInternalNote: record.person?.internalNote || '',
  personIsActive: record.person?.isActive !== false,
});

const AdminInstructorsEditPage = () => {
  const content = yowanPageContent['/admin/events/instructors/:instructorId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { instructorId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [memberOptions, setMemberOptions] = useState(instructorMemberPlaceholderOptions);
  const [personOptions, setPersonOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingPersonOptions, setIsLoadingPersonOptions] = useState(false);
  const [optionLoadError, setOptionLoadError] = useState('');
  const [personOptionLoadError, setPersonOptionLoadError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [accessMessage, setAccessMessage] = useState('');
  const [membershipMessage, setMembershipMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isAccessSubmitting, setIsAccessSubmitting] = useState(false);
  const [isMembershipSubmitting, setIsMembershipSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formContent = {
    ...content,
    form: {
      ...content?.form,
      options: {
        ...content?.form?.options,
        members: memberOptions,
      },
    },
  };
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord
        ? { ...currentRecord, ...getPersonLedgerDefaults(currentRecord), image: '' }
        : {
            instructorName: '',
            instructorEmail: '',
            memberId: '',
            personalUrl: '',
            webLinkWebsite: '',
            webLinkFacebook: '',
            webLinkX: '',
            webLinkInstagram: '',
            webLinkYoutube: '',
            webLinkTiktok: '',
            webLinkOther: '',
            roleLabel: '',
            biography: '',
            career: '',
            image: '',
            specialties: '',
            isActive: true,
            publicProfileEnabled: false,
            remarks: '',
            personId: '',
            personDisplayName: '',
            personNameKana: '',
            personEmail: '',
            personPhone: '',
            personOrganization: '',
            personRoleTitle: '',
            personInternalNote: '',
            personIsActive: true,
        },
  });

  useEffect(() => {
    if (!content || !instructorId) {
      return undefined;
    }

    let isMounted = true;

    const loadInstructor = async () => {
      setIsLoading(true);
      setSubmitMessage('');

      try {
        const record = await fetchYowanAdminInstructorDetail(instructorId);

        if (!isMounted) {
          return;
        }

        let fullPerson = null;
        const recordPersonId = resolveRecordPersonId(record);

        if (recordPersonId) {
          try {
            fullPerson = await fetchYowanAdminPersonDetail(recordPersonId);
          } catch (personError) {
            fullPerson = null;
          }
        }

        if (!isMounted) {
          return;
        }

        const hydratedRecord = {
          ...record,
          personId: recordPersonId || fullPerson?.id || '',
          person: mergePersonLedgerRecord(record.person, fullPerson),
        };

        setCurrentRecord(hydratedRecord);
        setInviteEmail(hydratedRecord.instructorAuthSummary?.loginEmail || hydratedRecord.instructorAccess?.loginEmail || hydratedRecord.instructorEmail || '');
        reset({ ...hydratedRecord, ...getPersonLedgerDefaults(hydratedRecord), image: '' });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setSubmitMessage('講師を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadInstructor();

    return () => {
      isMounted = false;
    };
  }, [content, instructorId, reset]);

  useEffect(() => {
    if (!content || !currentRecord) {
      return undefined;
    }

    let isMounted = true;

    const loadMemberOptions = async () => {
      setIsLoadingOptions(true);
      setOptionLoadError('');

      try {
        const options = await fetchYowanAdminMemberOptions({
          profileKind: 'instructor',
          includeMemberId: currentRecord.memberId,
        });

        if (isMounted) {
          setMemberOptions(mergeCurrentMemberOption([
            { value: '', label: '未紐づき / 外部講師' },
            ...options,
          ], currentRecord));
        }
      } catch (error) {
        if (isMounted) {
          setOptionLoadError('会員選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadMemberOptions();

    return () => {
      isMounted = false;
    };
  }, [content, currentRecord, setValue, watch]);

  useEffect(() => {
    setMemberOptions((currentOptions) => mergeCurrentMemberOption(currentOptions, currentRecord));
  }, [currentRecord]);

  useEffect(() => {
    if (!content || !currentRecord) {
      return undefined;
    }

    let isMounted = true;

    const loadPersonOptions = async () => {
      setIsLoadingPersonOptions(true);
      setPersonOptionLoadError('');

      try {
        const people = await fetchYowanAdminPersonList();

        if (isMounted) {
          const matchedPerson = findMatchingPersonLedger(people.results, currentRecord);
          const hydratedPerson = mergePersonLedgerRecord(currentRecord.person, matchedPerson);
          const hydratedRecord = hydratedPerson
            ? {
                ...currentRecord,
                personId: resolveRecordPersonId(currentRecord) || hydratedPerson.id || '',
                person: hydratedPerson,
              }
            : currentRecord;

          if (!hasSamePersonLedgerValues(currentRecord.person, hydratedRecord.person)) {
            setCurrentRecord(hydratedRecord);
          }

          setPersonOptions(buildYowanPersonOptions(people.results, hydratedRecord.person));

          if (hydratedPerson && (watch?.('personId') || resolveRecordPersonId(currentRecord) || hydratedPerson.id) === hydratedPerson.id) {
            applyPersonLedgerValues(setValue, hydratedPerson);
          }
        }
      } catch (error) {
        if (isMounted) {
          setPersonOptionLoadError('人物台帳の選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPersonOptions(false);
        }
      }
    };

    loadPersonOptions();

    return () => {
      isMounted = false;
    };
  }, [content, currentRecord, setValue, watch]);

  useEffect(() => {
    const person = currentRecord?.person;

    if (!person?.id) {
      return;
    }

    const hasPersonOption = personOptions.some((option) => option.value === person.id);

    if (hasPersonOption) {
      applyPersonLedgerValues(setValue, person);
    }
  }, [currentRecord?.person, personOptions, setValue]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      const personId = await resolveYowanAdminPersonId(values, currentRecord?.person);

      await updateYowanAdminInstructor(instructorId, { ...values, personId });
      setSubmitMessage('保存しました。');
      navigate(appendYowanPreviewQueryIfNeeded('/admin/events/instructors', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPrimaryAccessAction = () => {
    const action = currentRecord?.instructorAuthSummary?.recommendedAction;

    return ['send_auth_invite', 'grant_access', 'resend_invite'].includes(action)
      ? action
      : '';
  };

  const updateCurrentAccess = (result) => {
    const access = result?.access || result;
    const authSummary = result?.authSummary || null;

    setCurrentRecord((current) => current ? {
      ...current,
      instructorAccess: access || current.instructorAccess,
      instructorAuthSummary: authSummary || current.instructorAuthSummary,
    } : current);

    const nextEmail = authSummary?.loginEmail || access?.loginEmail;

    if (nextEmail) {
      setInviteEmail(nextEmail);
    }
  };

  const runAccessAction = async (action, successMessage) => {
    setIsAccessSubmitting(true);
    setAccessMessage('処理しています。');

    try {
      const result = await action();
      updateCurrentAccess(result);
      setAccessMessage(successMessage);
    } catch (error) {
      setAccessMessage(getYowanAdminMutationErrorMessage(error, 'ログイン権限の処理に失敗しました。入力内容と権限を確認してください。'));
    } finally {
      setIsAccessSubmitting(false);
    }
  };

  const handlePrimaryAccessAction = () => {
    const action = getPrimaryAccessAction();
    const sendInvite = action !== 'grant_access';
    const successMessages = {
      send_auth_invite: '認証招待を送信しました。',
      grant_access: '講師の閲覧権限を付与しました。',
      resend_invite: '招待メールを再送しました。',
    };

    runAccessAction(
      () => inviteYowanAdminInstructorAccess({
        instructorId,
        email: inviteEmail || currentRecord?.instructorEmail,
        sendInvite,
      }),
      successMessages[action] || 'ログイン権限を更新しました。',
    );
  };

  const handleEnsureMembership = async () => {
    const authSubjectId =
      currentRecord?.instructorAuthSummary?.authSubjectId ||
      currentRecord?.instructorAccess?.authSubjectId ||
      '';

    if (!authSubjectId) {
      setMembershipMessage(content.form.membershipAccess?.authRequiredText || '先に認証主体を作成してください。');
      return;
    }

    setIsMembershipSubmitting(true);
    setMembershipMessage(content.form.membershipAccess?.pendingMessage || '会員資格を付与しています。');

    try {
      const result = await ensureYowanAdminMemberMembership({
        authSubjectId,
        name: currentRecord?.instructorName || '',
        email: currentRecord?.instructorAuthSummary?.loginEmail || currentRecord?.instructorAccess?.loginEmail || currentRecord?.instructorEmail || '',
        sendPasswordSetup: true,
      });
      const member = result.member || {};

      setCurrentRecord((current) => current ? {
        ...current,
        memberId: member.id || current.memberId,
        member: member.id ? { id: member.id, name: member.name, email: member.email } : current.member,
        memberName: member.name || current.memberName,
        memberEmail: member.email || current.memberEmail,
      } : current);
      setMembershipMessage(content.form.membershipAccess?.successMessage || '会員資格を付与しました。');
    } catch (error) {
      setMembershipMessage(getYowanAdminMutationErrorMessage(error, content.form.membershipAccess?.errorMessage || '会員資格を付与できませんでした。'));
    } finally {
      setIsMembershipSubmitting(false);
    }
  };

  const authSummary = currentRecord?.instructorAuthSummary || {};
  const primaryAccessAction = getPrimaryAccessAction();
  const primaryAccessActionLabel =
    content.form.stakeholderAccess?.actionLabels?.[primaryAccessAction] ||
    authSummary.recommendedActionLabel ||
    '認証招待を送信';
  const lastInvitedAt = authSummary.lastInvitedAt || currentRecord?.instructorAccess?.lastInvitedAt || '';
  const membershipContent = content.form.membershipAccess || {};
  const membershipAuthSubjectId = authSummary.authSubjectId || currentRecord?.instructorAccess?.authSubjectId || '';
  const hasMemberProfile = Boolean(currentRecord?.memberId || currentRecord?.member?.id);

  return (
    <AdminBlogShell activeNavHref="/admin/events/instructors" routeKey="/admin/events/instructors/:instructorId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/instructors', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      {isLoading || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : submitMessage || '講師が見つかりませんでした。'}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          {isLoadingOptions ? <LoadingSpinner size="sm" /> : null}
          {optionLoadError ? <p className="yowan-admin-form__message">{optionLoadError}</p> : null}
          <AdminInstructorEditorFields
            content={formContent}
            register={register}
            errors={errors}
            currentImage={currentRecord?.primary_image}
          />
          <AdminPersonLedgerSection
            content={formContent}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            personOptions={personOptions}
            currentPerson={currentRecord?.person}
            isLoadingOptions={isLoadingPersonOptions}
            optionLoadError={personOptionLoadError}
          />

          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">{content.form.stakeholderAccess?.title || '講師ログイン権限'}</h2>
              <p className="yowan-admin-detail-panel__lead">
                {content.form.stakeholderAccess?.lead || '講師が自分に関係するセミナー、申込、申込メッセージだけを閲覧できるようにします。'}
              </p>
            </header>
            <div className="yowan-admin-event-detail__grid">
              <label className="c-form__field">
                <span className="c-form__label">{content.form.stakeholderAccess?.emailLabel || 'ログインメールアドレス'}</span>
                <input
                  className="c-form__control"
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  disabled={isAccessSubmitting}
                />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">認証</span>
                <input className="c-form__control" readOnly type="text" value={authSummary.authSubjectExists ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">閲覧権限</span>
                <input className="c-form__control" readOnly type="text" value={authSummary.accessStatusLabel || currentRecord.instructorAccess?.statusLabel || '未付与'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">パスワード</span>
                <input className="c-form__control" readOnly type="text" value={authSummary.passwordSetLabel || currentRecord.instructorAccess?.passwordSetLabel || '-'} />
              </label>
              {lastInvitedAt ? (
                <label className="c-form__field yowan-admin-readonly-field">
                  <span className="c-form__label">最終招待</span>
                  <input className="c-form__control" readOnly type="text" value={lastInvitedAt} />
                </label>
              ) : null}
            </div>
            {authSummary.recommendedActionReasonLabel ? (
              <p className="yowan-admin-form__field-note">{authSummary.recommendedActionReasonLabel}</p>
            ) : null}
            {primaryAccessAction ? (
              <div className="yowan-admin-form__actions">
                <button type="button" className="c-form__submit" disabled={isAccessSubmitting} onClick={handlePrimaryAccessAction}>
                  {primaryAccessActionLabel}
                </button>
              </div>
            ) : (
              <p className="yowan-admin-form__field-note">
                {content.form.stakeholderAccess?.readyText || '認証主体と閲覧権限は設定済みです。'}
              </p>
            )}
            {isAccessSubmitting ? (
              <p className="yowan-admin-form__message yowan-admin-form__message--pending">
                <LoadingSpinner inline size="sm" />
                <span>{accessMessage}</span>
              </p>
            ) : accessMessage ? (
              <p className="yowan-admin-form__message">{accessMessage}</p>
            ) : null}
          </section>

          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">{membershipContent.title || '会員資格'}</h2>
              <p className="yowan-admin-detail-panel__lead">
                {membershipContent.lead || '講師が会員でもある場合に、同じログインアカウントへ会員プロフィールと会員資格を付与します。'}
              </p>
            </header>
            <div className="yowan-admin-event-detail__grid">
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.authLabel || '認証主体'}</span>
                <input className="c-form__control" readOnly type="text" value={membershipAuthSubjectId ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.memberLabel || '会員プロフィール'}</span>
                <input className="c-form__control" readOnly type="text" value={hasMemberProfile ? '作成済み' : '未作成'} />
              </label>
              <label className="c-form__field yowan-admin-readonly-field">
                <span className="c-form__label">{membershipContent.emailLabel || '対象メールアドレス'}</span>
                <input
                  className="c-form__control"
                  readOnly
                  type="text"
                  value={authSummary.loginEmail || currentRecord?.instructorAccess?.loginEmail || currentRecord?.instructorEmail || '-'}
                />
              </label>
            </div>
            {hasMemberProfile ? (
              <p className="yowan-admin-form__field-note">{membershipContent.readyText || '会員プロフィールは作成済みです。'}</p>
            ) : membershipAuthSubjectId ? (
              <div className="yowan-admin-form__actions">
                <button type="button" className="c-form__submit" disabled={isMembershipSubmitting} onClick={handleEnsureMembership}>
                  {membershipContent.actionLabel || '会員資格を付与'}
                </button>
              </div>
            ) : (
              <p className="yowan-admin-form__field-note">{membershipContent.authRequiredText || '先に認証主体を作成してください。'}</p>
            )}
            {isMembershipSubmitting ? (
              <p className="yowan-admin-form__message yowan-admin-form__message--pending">
                <LoadingSpinner inline size="sm" />
                <span>{membershipMessage}</span>
              </p>
            ) : membershipMessage ? (
              <p className="yowan-admin-form__message">{membershipMessage}</p>
            ) : null}
          </section>

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {content.form.submitLabel}
            </button>
          </div>

          {isSubmitting ? (
            <p className="yowan-admin-form__message yowan-admin-form__message--pending">
              <LoadingSpinner inline size="sm" />
              <span>{content.form.pendingMessage}</span>
            </p>
          ) : submitMessage ? (
            <p className="yowan-admin-form__message">{submitMessage}</p>
          ) : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminInstructorsEditPage;
